/* eslint-disable @typescript-eslint/ban-types */
export enum CliColor {
  Red = `\x1b[31m`,
  Green = `\x1b[32m`,
  Blue = `\x1b[34m`,
  Reset = `\x1b[0m`,
}

/**
 * Extended DOM Element interface that includes Panel and WebElement methods, props, etc.
 */
export interface IsoElm extends Element {
  attr?: (attrName: string) => any;
  state?: Record<PropertyKey, any>;
  update?: Function;
  disconnectedCallback?: Function;
  el?: Element;
  getConfig?: (key: string) => any;
  value?: any;
  isAttributeEnabled?: (key: string) => boolean;
  // panel elements may have any of their own props/methods;
  // these may be called in tests so we must support them here
  [key: string]: any;
}

// ref.with method - allow broad or specific syntax:
// ref.with({attrs, props, state}, function () { ... });
// ref.with.attrs(attrs, function () { ... });
// etc.
type AttrsPropsStateObj = {
  attrs?: Obj;
  props?: Obj;
  state?: Obj;
};
type RefWithFunc = (obj: AttrsPropsStateObj, testCases: Function) => Promise<void>;
interface RefWithObj {
  attrs?: (attrs: Obj, testCases: Function) => Promise<void>;
  props?: (props: Obj, testCases: Function) => Promise<void>;
  state?: (state: Obj, testCases: Function) => Promise<void>;
  appState?: (appState: Obj, testCases: Function) => Promise<void>;
}
type RefWith = RefWithFunc & RefWithObj;

/**
 * DOM Element wrapper interface that provides concise access to test-relevant DOM
 * elements explicitly defined in our code.
 */
export interface IsoRef extends Object {
  root: IsoElm;
  isRef: true;

  _results: {[key: string]: IsoElm[]};
  _refName: string | null;
  _tagName: string | null;
  _selector: string | null;
  _get: (index?: number) => IsoElm[];
  _reset: (newRoot: IsoElm, refParams: unknown) => IsoRef;

  first: () => IsoElm;
  last: () => IsoElm;
  nth: (index: number) => IsoElm;
  all: () => IsoElm[];
  exists: (index?: number) => boolean;

  requery: (newRoot?: IsoElm) => IsoRef;
  ref: (refName: string) => IsoRef;
  tag: (tagName: string) => IsoRef;
  sel: (selector: string) => IsoRef;

  el: IsoElm;
  attr: (key: string, val?: any) => any;
  isAttributeEnabled?: (attrName: string) => boolean;
  with: RefWith;
  log: () => void;
}

export interface IsoElementConfig {
  tagName?: string;
  attrs?: Record<string, any>;
  props?: Record<string, any>;
  state?: Record<string, any>;
}

export interface IsoDescribeElementInitFunc {
  (tagName: string, ...elementConfigs: IsoElementConfig[]): IsoRef;
  (...elementConfigs: IsoElementConfig[]): IsoRef;
}

export type IsoDescribeElementRenderFunc = (isoElmOrRef: IsoElm | IsoRef) => Promise<IsoRef>;
export type IsoDescribeElementTestCasesFunc = (
  initFunc: IsoDescribeElementInitFunc,
  renderFunc?: IsoDescribeElementRenderFunc,
) => void;

export interface IsoTestConstruct {
  (testName: string, testFunc: IsoDescribeElementTestCasesFunc): void;
  node?: IsoTestConstruct;
  browser?: IsoTestConstruct;
  only?: IsoTestConstruct;
  skip?: IsoTestConstruct;
  element?: IsoTestConstruct;
}
